import type { ReactElement } from 'react';
import { formatDate } from '@xdocs/i18n';
import type { AuditEvent } from '@xdocs/schemas';
import { Modal, cn, useI18n } from '@xdocs/ui';
import { useAuditT } from './useAuditT.js';
import styles from './AuditDetailPanel.module.css';

export interface AuditDetailPanelProps {
  event: AuditEvent;
  onClose: () => void;
}

/** F59 detail — expandable `metadataJsonb` before/after + reason (read-only). */
export function AuditDetailPanel({ event, onClose }: AuditDetailPanelProps): ReactElement {
  const { locale } = useI18n();
  const { t } = useAuditT();
  const hasMetadata = event.metadataJsonb !== null && Object.keys(event.metadataJsonb).length > 0;

  return (
    <Modal open onClose={onClose} title={t('adminAudit:detail.title')} size="md" scrollableBody>
      <dl className={cn(styles['fields'])}>
        <div>
          <dt>{t('adminAudit:detail.field.timestamp')}</dt>
          <dd>{formatDate(locale, event.createdAt, { dateStyle: 'medium', timeStyle: 'medium' })}</dd>
        </div>
        <div>
          <dt>{t('adminAudit:detail.field.actor')}</dt>
          <dd className={cn(styles['mono'])}>{event.actorUserId}</dd>
        </div>
        <div>
          <dt>{t('adminAudit:detail.field.action')}</dt>
          <dd>{t(`adminAudit:action.${event.action}`)}</dd>
        </div>
        <div>
          <dt>{t('adminAudit:detail.field.target')}</dt>
          <dd className={cn(styles['mono'])}>
            {event.targetType && event.targetId ? `${event.targetType}:${event.targetId}` : t('adminAudit:target.deleted')}
          </dd>
        </div>
        {event.impersonatedUserId && (
          <div>
            <dt>{t('adminAudit:detail.field.impersonated')}</dt>
            <dd className={cn(styles['mono'])}>{event.impersonatedUserId}</dd>
          </div>
        )}
        {event.ip && (
          <div>
            <dt>{t('adminAudit:detail.field.ip')}</dt>
            <dd className={cn(styles['mono'])}>{event.ip}</dd>
          </div>
        )}
        {event.userAgent && (
          <div>
            <dt>{t('adminAudit:detail.field.userAgent')}</dt>
            <dd>{event.userAgent}</dd>
          </div>
        )}
      </dl>

      <p className={cn(styles['metadataLabel'])}>{t('adminAudit:detail.field.metadata')}</p>
      {hasMetadata ? (
        <pre className={cn(styles['metadata'])}>{JSON.stringify(event.metadataJsonb, null, 2)}</pre>
      ) : (
        <p className={cn(styles['metadataEmpty'])}>{t('adminAudit:detail.metadata.none')}</p>
      )}
    </Modal>
  );
}
